/**
 * APDialog Component
 *
 * A generic dialog/modal container component following AstroPay design system.
 * Provides backdrop, positioning, and basic modal behavior.
 *
 * @example
 * ```tsx
 * <APDialog isOpen={isOpen} onClose={handleClose} size="medium">
 *   <div>Dialog content</div>
 * </APDialog>
 * ```
 */

import { forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

import clsx from 'clsx';

import type { IAPDialogProps } from './types';

import './styles.scss';

/**
 * APDialog - Generic dialog container
 *
 * Provides the foundational structure for modal dialogs with backdrop,
 * positioning, and accessibility features.
 */
export const APDialog = forwardRef<HTMLDivElement, IAPDialogProps>(
  (
    {
      isOpen,
      onClose,
      children,
      size = 'medium',
      showBackdrop = true,
      closeOnBackdropClick = true,
      className,
      ariaLabel,
      ariaDescribedBy,
      'data-testid': testId,
    },
    ref
  ) => {
    // Handle ESC key
    useEffect(() => {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'unset';
      };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && closeOnBackdropClick) {
        onClose();
      }
    };

    const dialogClasses = clsx('ap-dialog', `ap-dialog--${size}`, className);

    const dialogContent = (
      <>
        {showBackdrop && (
          <div className="ap-dialog__backdrop" onClick={handleBackdropClick} role="presentation" />
        )}
        <div
          ref={ref}
          className={dialogClasses}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          data-testid={testId}
        >
          {children}
        </div>
      </>
    );

    return createPortal(dialogContent, document.body);
  }
);

APDialog.displayName = 'APDialog';

export type { IAPDialogProps };
export default APDialog;
